'use client';

import { useState, useEffect, useRef } from 'react';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';

export function OfflineBanner() {
  const { isOnline } = useOnlineStatus();
  const [showReconnected, setShowReconnected] = useState(false);
  const wasOfflineRef = useRef(false);

  useEffect(() => {
    if (!isOnline) {
      wasOfflineRef.current = true;
      // Defer setState to avoid synchronous state update inside an effect
      // (react-hooks/set-state-in-effect). Using a 0ms timer keeps the same
      // immediate-looking UX while satisfying the rule.
      const clear = setTimeout(() => setShowReconnected(false), 0);
      return () => clearTimeout(clear);
    } else if (wasOfflineRef.current) {
      wasOfflineRef.current = false;
      const show = setTimeout(() => setShowReconnected(true), 0);

      const dismiss = setTimeout(() => {
        setShowReconnected(false);
      }, 3000);

      return () => {
        clearTimeout(show);
        clearTimeout(dismiss);
      };
    }
  }, [isOnline]);

  if (isOnline && !showReconnected) {
    return null;
  }

  if (!isOnline) {
    return (
      <div
        role="status"
        aria-live="assertive"
        // WCAG 4.1.3 (F7): atomic announcements ensure AT reads the full
        // current message every time the banner content changes (e.g.
        // "You are offline." → "Back online. Syncing...") rather than
        // diffing against the previous announcement.
        aria-atomic="true"
        // UI-03: `pointer-events-none` on the full-width wrapper. This strip
        // is ~52px tall and sits at z-60 ABOVE the fixed z-50 h-16 navbar, so
        // without it the transparent wrapper swallowed every click across the
        // top of the page — logo, sidebar toggle, hamburger, wallet chip and
        // notification bell all looked interactive and did nothing while the
        // banner (or the three-second "Back online" toast) was showing.
        className="pointer-events-none fixed top-0 inset-x-0 z-[60] flex items-center justify-center px-4 py-2 font-ui"
      >
        <div className="pointer-events-auto bg-warning/10 text-warning rounded-[12px] px-4 py-2 text-sm font-medium shadow-card">
          You are offline. Changes will sync when you reconnect.
        </div>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      // UI-03 — see the offline branch above. The reconnected toast covers the
      // navbar for three seconds and blocked it just the same.
      className="pointer-events-none fixed top-0 inset-x-0 z-[60] flex items-center justify-center px-4 py-2 font-ui"
    >
      <div className="pointer-events-auto bg-secondary/10 text-secondary rounded-[12px] px-4 py-2 text-sm font-medium shadow-card">
        Back online. Syncing...
      </div>
    </div>
  );
}
